import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import {
  GetApp as ExportIcon,
} from '@mui/icons-material';
import { reservationsApi, guestsApi, roomsApi, financialApi } from '../services/api';
import { format, subDays, subMonths } from 'date-fns';

interface ReportData {
  occupancy?: any;
  revenue?: any;
  guests?: any;
  reservations?: any;
}

const Reports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportType, setReportType] = useState<string>('occupancy');
  const [dateRange, setDateRange] = useState({
    start: format(subMonths(new Date(), 1), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });
  const [reportData, setReportData] = useState<ReportData>({});

  useEffect(() => {
    fetchReport();
  }, [reportType, dateRange]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      switch (reportType) {
        case 'occupancy':
          await fetchOccupancyReport();
          break;
        case 'revenue':
          await fetchRevenueReport();
          break;
        case 'guests':
          await fetchGuestReport();
          break;
        case 'reservations':
          await fetchReservationReport();
          break;
        default:
          break;
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const fetchOccupancyReport = async () => {
    const [roomsRes, reservationsRes] = await Promise.all([
      roomsApi.getAll(),
      reservationsApi.getAll({
        start_date: dateRange.start,
        end_date: dateRange.end,
      }),
    ]);

    const rooms = roomsRes.data.data || [];
    const reservations = reservationsRes.data.data || [];
    const totalRooms = rooms.length;
    const occupiedRooms = reservations.filter((r: any) => 
      r.status === 'checked_in' || r.status === 'confirmed'
    ).length;

    const occupancyByDate: { [key: string]: number } = {};
    reservations.forEach((res: any) => {
      const checkIn = new Date(res.check_in_date);
      const checkOut = new Date(res.check_out_date);
      let current = new Date(checkIn);
      
      while (current <= checkOut) {
        const dateKey = format(current, 'yyyy-MM-dd');
        if (dateKey >= dateRange.start && dateKey <= dateRange.end) {
          occupancyByDate[dateKey] = (occupancyByDate[dateKey] || 0) + 1;
        }
        current.setDate(current.getDate() + 1);
      }
    });

    setReportData({
      occupancy: {
        totalRooms,
        occupiedRooms,
        occupancyRate: totalRooms > 0 ? ((occupiedRooms / totalRooms) * 100).toFixed(2) : 0,
        occupancyByDate,
        reservations,
      },
    });
  };

  const fetchRevenueReport = async () => {
    const [summaryRes, revenueRes] = await Promise.all([
      financialApi.getSummary({
        start_date: dateRange.start,
        end_date: dateRange.end,
      }),
      financialApi.getRevenue({
        start_date: dateRange.start,
        end_date: dateRange.end,
      }),
    ]);

    setReportData({
      revenue: {
        summary: summaryRes.data.data,
        daily: revenueRes.data.data || [],
      },
    });
  };

  const fetchGuestReport = async () => {
    const guestsRes = await guestsApi.getAll();
    const reservationsRes = await reservationsApi.getAll({
      start_date: dateRange.start,
      end_date: dateRange.end,
    });

    const guests = guestsRes.data.data || [];
    const reservations = reservationsRes.data.data || [];

    const guestStats = guests.map((guest: any) => {
      const guestReservations = reservations.filter((r: any) => r.guest.id === guest.id);
      const totalSpent = guestReservations.reduce((sum: number, r: any) => sum + (r.paid_amount || 0), 0);
      return {
        ...guest,
        reservationCount: guestReservations.length,
        totalSpent,
      };
    }).sort((a: any, b: any) => b.totalSpent - a.totalSpent);

    setReportData({
      guests: {
        total: guests.length,
        newGuests: guests.filter((g: any) => 
          new Date(g.created_at) >= new Date(dateRange.start)
        ).length,
        topGuests: guestStats.slice(0, 10),
        allGuests: guestStats,
      },
    });
  };

  const fetchReservationReport = async () => {
    const reservationsRes = await reservationsApi.getAll({
      start_date: dateRange.start,
      end_date: dateRange.end,
    });

    const reservations = reservationsRes.data.data || [];
    
    const statusCounts: { [key: string]: number } = {};
    reservations.forEach((res: any) => {
      statusCounts[res.status] = (statusCounts[res.status] || 0) + 1;
    });

    const totalRevenue = reservations.reduce((sum: number, r: any) => sum + (r.total_amount || 0), 0);
    const totalPaid = reservations.reduce((sum: number, r: any) => sum + (r.paid_amount || 0), 0);

    setReportData({
      reservations: {
        total: reservations.length,
        statusCounts,
        totalRevenue,
        totalPaid,
        outstanding: totalRevenue - totalPaid,
        reservations,
      },
    });
  };

  const handleExport = () => {
    // Simple CSV export
    let csv = '';
    
    switch (reportType) {
      case 'occupancy':
        csv = 'Date,Occupied Rooms\n';
        Object.entries(reportData.occupancy?.occupancyByDate || {}).forEach(([date, count]) => {
          csv += `${date},${count}\n`;
        });
        break;
      case 'revenue':
        csv = 'Date,Income,Expenses,Profit\n';
        reportData.revenue?.daily.forEach((item: any) => {
          csv += `${item.date},${item.income},${item.expense},${item.profit}\n`;
        });
        break;
      case 'guests':
        csv = 'Name,Email,Reservations,Total Spent\n';
        reportData.guests?.topGuests.forEach((guest: any) => {
          csv += `"${guest.first_name} ${guest.last_name}",${guest.email || ''},${guest.reservationCount},${guest.totalSpent}\n`;
        });
        break;
      case 'reservations':
        csv = 'ID,Guest,Room,Check-In,Check-Out,Status,Amount,Paid\n';
        reportData.reservations?.reservations.forEach((res: any) => {
          csv += `${res.id},"${res.guest.name}",${res.room?.number || 'TBD'},${res.check_in_date},${res.check_out_date},${res.status},${res.total_amount},${res.paid_amount}\n`;
        });
        break;
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportType}_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const renderOccupancyReport = () => {
    if (!reportData.occupancy) return null;

    const { totalRooms, occupiedRooms, occupancyRate, reservations } = reportData.occupancy;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Rooms</Typography>
              <Typography variant="h4">{totalRooms}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Occupied Rooms</Typography>
              <Typography variant="h4" color="primary.main">{occupiedRooms}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Occupancy Rate</Typography>
              <Typography variant="h4" color="success.main">{occupancyRate}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Guest</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Check-In</TableCell>
                  <TableCell>Check-Out</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.slice(0, 20).map((res: any) => (
                  <TableRow key={res.id}>
                    <TableCell>{res.guest.name}</TableCell>
                    <TableCell>{res.room?.number || 'TBD'}</TableCell>
                    <TableCell>{format(new Date(res.check_in_date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>{format(new Date(res.check_out_date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      <Chip label={res.status.replace('_', ' ')} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  const renderRevenueReport = () => {
    if (!reportData.revenue) return null;

    const { summary, daily } = reportData.revenue;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Income</Typography>
              <Typography variant="h4" color="success.main">
                ${summary.total_income.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Expenses</Typography>
              <Typography variant="h4" color="error.main">
                ${summary.total_expenses.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Net Profit</Typography>
              <Typography variant="h4" color={summary.net_profit >= 0 ? 'success.main' : 'error.main'}>
                ${summary.net_profit.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Payments</Typography>
              <Typography variant="h4" color="info.main">
                ${summary.total_payments.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Income</TableCell>
                  <TableCell>Expenses</TableCell>
                  <TableCell>Profit</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {daily.map((item: any, index: number) => (
                  <TableRow key={index}>
                    <TableCell>{format(new Date(item.date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell color="success.main">${item.income.toFixed(2)}</TableCell>
                    <TableCell color="error.main">${item.expense.toFixed(2)}</TableCell>
                    <TableCell color={item.profit >= 0 ? 'success.main' : 'error.main'}>
                      ${item.profit.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  const renderGuestReport = () => {
    if (!reportData.guests) return null;

    const { total, newGuests, topGuests } = reportData.guests;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Guests</Typography>
              <Typography variant="h4">{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>New Guests (Period)</Typography>
              <Typography variant="h4" color="primary.main">{newGuests}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Top Guests by Revenue</Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Reservations</TableCell>
                  <TableCell>Total Spent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topGuests.map((guest: any) => (
                  <TableRow key={guest.id}>
                    <TableCell>{guest.first_name} {guest.last_name}</TableCell>
                    <TableCell>{guest.email || '-'}</TableCell>
                    <TableCell>{guest.reservationCount}</TableCell>
                    <TableCell>${guest.totalSpent.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  const renderReservationReport = () => {
    if (!reportData.reservations) return null;

    const { total, statusCounts, totalRevenue, totalPaid, outstanding, reservations } = reportData.reservations;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Reservations</Typography>
              <Typography variant="h4">{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Revenue</Typography>
              <Typography variant="h4" color="success.main">${totalRevenue.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Paid</Typography>
              <Typography variant="h4" color="info.main">${totalPaid.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Outstanding</Typography>
              <Typography variant="h4" color="warning.main">${outstanding.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>Status Breakdown</Typography>
          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {Object.entries(statusCounts).map(([status, count]) => (
              <Chip key={status} label={`${status.replace('_', ' ')}: ${count}`} />
            ))}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Guest</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Check-In</TableCell>
                  <TableCell>Check-Out</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Paid</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservations.slice(0, 50).map((res: any) => (
                  <TableRow key={res.id}>
                    <TableCell>{res.guest.name}</TableCell>
                    <TableCell>{res.room?.number || 'TBD'}</TableCell>
                    <TableCell>{format(new Date(res.check_in_date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>{format(new Date(res.check_out_date), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      <Chip label={res.status.replace('_', ' ')} size="small" />
                    </TableCell>
                    <TableCell>${res.total_amount.toFixed(2)}</TableCell>
                    <TableCell>${res.paid_amount.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Reports</Typography>
        <Button
          variant="outlined"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          disabled={loading || !reportData[reportType as keyof ReportData]}
        >
          Export CSV
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            select
            label="Report Type"
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
          >
            <MenuItem value="occupancy">Occupancy Report</MenuItem>
            <MenuItem value="revenue">Revenue Report</MenuItem>
            <MenuItem value="guests">Guest Report</MenuItem>
            <MenuItem value="reservations">Reservation Report</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            type="date"
            label="End Date"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Button
            fullWidth
            variant="contained"
            onClick={fetchReport}
            disabled={loading}
            sx={{ height: '56px' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Generate Report'}
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {reportType === 'occupancy' && renderOccupancyReport()}
          {reportType === 'revenue' && renderRevenueReport()}
          {reportType === 'guests' && renderGuestReport()}
          {reportType === 'reservations' && renderReservationReport()}
        </>
      )}
    </Box>
  );
};

export default Reports;

